"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiGet, apiPost, apiPut, setApiAlbum } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

/** Sentinel id of the virtual "All photos" view (mirrors albums::ALL_ALBUM_ID). */
export const ALL_ALBUM_ID = 0;

export type Album = {
  id: number;
  name: string;
  userId: number;
  owner: string;
  photoCount: number;
  owned: boolean;
  canContribute: boolean;
};

type ListResponse = {
  own: Album[];
  shared: Album[];
  all: Album | null;
  current: number;
};

type AlbumContextValue = {
  /** Albums the signed-in user owns. */
  own: Album[];
  /** Albums other people have shared with them. */
  shared: Album[];
  /** The virtual "All photos" entry, when there is more than one album. */
  all: Album | null;
  current: number;
  /** The currently selected album, resolved from any section. */
  currentAlbum: Album | null;
  loading: boolean;
  selectAlbum: (id: number) => Promise<void>;
  createAlbum: (name: string) => Promise<Album>;
  refresh: () => Promise<void>;
};

const AlbumContext = createContext<AlbumContextValue | null>(null);

export function AlbumProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [own, setOwn] = useState<Album[]>([]);
  const [shared, setShared] = useState<Album[]>([]);
  const [all, setAll] = useState<Album | null>(null);
  const [current, setCurrent] = useState<number>(ALL_ALBUM_ID);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || user.mustChangePassword) return;
    setLoading(true);
    try {
      const data = await apiGet<ListResponse>("/albums");
      setOwn(data.own ?? []);
      setShared(data.shared ?? []);
      setAll(data.all ?? null);
      setCurrent(data.current ?? ALL_ALBUM_ID);
      setApiAlbum(String(data.current ?? ALL_ALBUM_ID));
    } catch {
      // A failed refresh leaves the previous list on screen rather than
      // blanking the sidebar mid-session.
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectAlbum = useCallback(async (id: number) => {
    await apiPut<{ ok: boolean; current: number }>("/albums/current", { id });
    setCurrent(id);
    setApiAlbum(String(id));
  }, []);

  const createAlbum = useCallback(
    async (name: string) => {
      const album = await apiPost<Album>("/albums", { name });
      await refresh();
      return album;
    },
    [refresh],
  );

  const currentAlbum = useMemo(() => {
    if (current === ALL_ALBUM_ID) return all;
    return (
      own.find((a) => a.id === current) ??
      shared.find((a) => a.id === current) ??
      null
    );
  }, [current, own, shared, all]);

  const value = useMemo(
    () => ({
      own,
      shared,
      all,
      current,
      currentAlbum,
      loading,
      selectAlbum,
      createAlbum,
      refresh,
    }),
    [own, shared, all, current, currentAlbum, loading, selectAlbum, createAlbum, refresh],
  );

  return <AlbumContext.Provider value={value}>{children}</AlbumContext.Provider>;
}

export function useAlbums() {
  const ctx = useContext(AlbumContext);
  if (!ctx) {
    throw new Error("useAlbums must be used inside an AlbumProvider");
  }
  return ctx;
}
